<template>
  <div class="ai-email-config">
    <div class="form-row">
      <div class="form-group">
        <div class="multiselect-wrap--small ai-email-multiselect">
          <multiselect
            v-model="selectedInbox"
            class="no-margin"
            track-by="id"
            label="name"
            :placeholder="$t('AUTOMATION.ACTION.AI_GENERATED_EMAIL.INBOX_PLACEHOLDER')"
            :options="inboxOptions"
            :allow-empty="false"
            :show-labels="false"
            :close-on-select="true"
            :max-height="180"
            @select="onInboxSelect"
          >
            <template #singleLabel="{ option }">
              <span v-tooltip="option.name" class="single-label-truncate">
                {{ option.name }}
              </span>
            </template>
            <template #option="{ option }">
              <span :title="option.name" class="option-truncate">
                {{ option.name }}
              </span>
            </template>
          </multiselect>
        </div>
      </div>

      <div class="form-group">
        <div class="macro-select-wrap">
          <div class="multiselect-wrap--small macro-multiselect ai-email-multiselect">
            <multiselect
              v-model="selectedMacro"
              class="no-margin"
              track-by="id"
              label="name"
              :placeholder="$t('AUTOMATION.ACTION.AI_GENERATED_EMAIL.MACRO_PLACEHOLDER')"
              :options="macroOptions"
              :allow-empty="false"
              :show-labels="false"
              :close-on-select="true"
              :max-height="180"
              @select="onMacroSelect"
            >
              <template #singleLabel="{ option }">
                <span v-tooltip="option.name" class="single-label-truncate">
                  {{ option.name }}
                </span>
              </template>
              <template #option="{ option }">
                <span :title="option.name" class="option-truncate">
                  {{ option.name }}
                </span>
              </template>
            </multiselect>
          </div>
          <fluent-icon
            v-tooltip="$t('AUTOMATION.ACTION.AI_GENERATED_EMAIL.MACRO_HELP')"
            icon="info"
            size="16"
            class="info-icon"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Multiselect from 'vue-multiselect';

export default {
  components: { Multiselect },
  props: {
    value: {
      type: [Object, Array],
      default: () => ({}),
    },
  },
  data() {
    return {
      localConfig: {
        inbox_id: null,
        macro_id: null,
      },
    };
  },
  computed: {
    ...mapGetters({
      emailInboxes: 'inboxes/getEmailInboxes',
      allMacros: 'macros/getMacros',
    }),
    inboxOptions() {
      return (this.emailInboxes || []).map(inbox => ({
        id: inbox.id,
        name: inbox.name,
      }));
    },
    macroOptions() {
      return (this.allMacros || [])
        .filter(macro => this.macroHasAutoExecuteApiCall(macro))
        .map(macro => ({ id: macro.id, name: macro.name }));
    },
    selectedInbox: {
      get() {
        return (
          this.inboxOptions.find(i => i.id === this.localConfig.inbox_id) ||
          null
        );
      },
      set() {
        // handled by @select
      },
    },
    selectedMacro: {
      get() {
        return (
          this.macroOptions.find(m => m.id === this.localConfig.macro_id) ||
          null
        );
      },
      set() {
        // handled by @select
      },
    },
  },
  watch: {
    value: {
      handler(newValue) {
        this.hydrateFromValue(newValue);
      },
      deep: true,
    },
  },
  mounted() {
    this.$store.dispatch('inboxes/get');
    this.$store.dispatch('macros/get');
    this.hydrateFromValue(this.value);
    this.emitUpdate();
  },
  methods: {
    macroHasAutoExecuteApiCall(macro) {
      if (!macro || !Array.isArray(macro.actions)) return false;
      return macro.actions.some(action => {
        if (action.action_name !== 'send_api_webhook') return false;
        const params = Array.isArray(action.action_params)
          ? action.action_params[0]
          : action.action_params;
        return params && params.auto_execute === true;
      });
    },
    hydrateFromValue(value) {
      const source = Array.isArray(value) ? value[0] : value;
      if (source && typeof source === 'object') {
        this.localConfig = {
          inbox_id: source.inbox_id ?? null,
          macro_id: source.macro_id ?? null,
        };
      }
    },
    onInboxSelect(option) {
      this.localConfig.inbox_id = option?.id ?? null;
      this.emitUpdate();
    },
    onMacroSelect(option) {
      this.localConfig.macro_id = option?.id ?? null;
      this.emitUpdate();
    },
    emitUpdate() {
      this.$emit('input', { ...this.localConfig });
    },
  },
};
</script>

<style scoped lang="scss">
.ai-email-config {
  @apply w-full;
}

.form-row {
  @apply flex flex-row gap-2 items-center;
}

.form-group {
  @apply flex flex-col flex-1 min-w-0;
}

.macro-select-wrap {
  @apply flex flex-row items-center gap-2 w-full;

  .macro-multiselect {
    @apply flex-1 min-w-0;
  }
}

.info-icon {
  @apply text-slate-400 dark:text-slate-500 cursor-help shrink-0;
}

.form-help {
  @apply text-xs text-slate-500 dark:text-slate-400 mt-1;
}

.form-help-warn {
  @apply text-yellow-600 dark:text-yellow-400;
}

.ai-email-multiselect {
  @apply w-full min-w-0;

  :deep(.multiselect) {
    @apply mb-0;
  }

  :deep(.multiselect__tags) {
    overflow: hidden !important;
  }

  :deep(.multiselect__single) {
    @apply block w-full whitespace-nowrap overflow-hidden;
    text-overflow: ellipsis;
  }

  .single-label-truncate,
  .option-truncate {
    @apply block w-full whitespace-nowrap overflow-hidden;
    text-overflow: ellipsis;
  }

  // Keep the open dropdown list constrained to the multiselect width and
  // truncate long option labels instead of causing a horizontal scroll.
  :deep(.multiselect__content-wrapper) {
    @apply w-full;
    overflow-x: hidden !important;
  }

  :deep(.multiselect__content) {
    @apply w-full max-w-full block;
  }

  :deep(.multiselect__option) {
    @apply block w-full whitespace-nowrap overflow-hidden;
    text-overflow: ellipsis;

    > span {
      @apply block w-full whitespace-nowrap overflow-hidden;
      text-overflow: ellipsis;
    }
  }
}
</style>
